const React = require("react");
const { useCJDropshipping } = require("../context/CJDropshippingContext");
const { useDropshippingResource } = require("../hooks/useDropshippingResource");
const { PageHeader, Button, ResourceState, StatusBadge } = require("../components/ui");
const h = React.createElement;

const AUTH_TYPES = {
  API_KEY: "api_key",
  ACCESS_TOKEN: "access_token",
  API_KEY_ACCESS_TOKEN: "api_key_access_token",
  API_KEY_SECRET: "api_key_secret",
};

const PAYMENT_MODES = { MANUAL: "manual", AUTOMATIC: "automatic" };

const DropshippingCJConnect = () => {
  const { api, t, isCJConnected } = useCJDropshipping();
  const connectionResource = useDropshippingResource(() => api.getConnection(), [api]);
  const [authType, setAuthType] = React.useState(AUTH_TYPES.API_KEY);
  const [apiKey, setApiKey] = React.useState("");
  const [accessToken, setAccessToken] = React.useState("");
  const [apiSecret, setApiSecret] = React.useState("");
  const [cjAccountEmail, setCjAccountEmail] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const authTypeWasSelected = React.useRef(false);

  // Mode de paiement des commandes CJ (manuel/automatique) -- indépendant du
  // formulaire de connexion ci-dessus, propre état busy/error/success pour ne
  // pas interférer avec les actions Connect/Test/Disconnect.
  const paymentSettingsResource = useDropshippingResource(() => api.getPaymentSettings(), [api]);
  const settings = paymentSettingsResource.data?.settings;
  const [paymentMode, setPaymentMode] = React.useState(PAYMENT_MODES.MANUAL);
  const [paymentModeTouched, setPaymentModeTouched] = React.useState(false);
  const [paymentBusy, setPaymentBusy] = React.useState(false);
  const [paymentError, setPaymentError] = React.useState("");
  const [paymentSuccess, setPaymentSuccess] = React.useState("");
  const [walletInfo, setWalletInfo] = React.useState(null);
  const [walletChecking, setWalletChecking] = React.useState(false);
  const [walletError, setWalletError] = React.useState("");

  React.useEffect(() => {
    if (settings && !paymentModeTouched) setPaymentMode(settings.paymentMode);
  }, [settings, paymentModeTouched]);

  const savePaymentMode = async () => {
    setPaymentBusy(true); setPaymentError(""); setPaymentSuccess("");
    try {
      await api.updatePaymentMode(paymentMode);
      setPaymentSuccess(t("h_cj_payment_mode_saved", "Mode de paiement enregistré."));
      setPaymentModeTouched(false);
      await paymentSettingsResource.reload();
    } catch (requestError) {
      setPaymentError(requestError.message);
    } finally {
      setPaymentBusy(false);
    }
  };

  const checkWalletBalance = async () => {
    setWalletChecking(true); setWalletError("");
    try {
      const result = await api.getWallet();
      setWalletInfo(result.wallet);
    } catch (requestError) {
      setWalletError(requestError.message);
    } finally {
      setWalletChecking(false);
    }
  };

  const wallet = walletInfo || settings?.cjWallet;

  const execute = async (action, successText, options = {}) => {
    const { clearCredentials = true, reload = true } = options;
    setBusy(true); setError(""); setSuccess("");
    try {
      await action();
      setSuccess(successText);
      if (clearCredentials) {
        setApiKey(""); setAccessToken(""); setApiSecret("");
      }
      if (reload) await connectionResource.reload();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };
  const connection = connectionResource.data?.connection;
  const authentication = connectionResource.data?.authentication || {};
  const supportedTypes = Array.isArray(authentication.supportedTypes)
    ? authentication.supportedTypes
    : [AUTH_TYPES.API_KEY, AUTH_TYPES.ACCESS_TOKEN, AUTH_TYPES.API_KEY_ACCESS_TOKEN];
  const supportedTypesKey = supportedTypes.join("|");
  React.useEffect(() => {
    const recommendedType = authentication.recommendedType;
    if (!authTypeWasSelected.current && recommendedType && supportedTypesKey.split("|").includes(recommendedType)) {
      setAuthType(recommendedType);
    }
  }, [authentication.recommendedType, supportedTypesKey]);
  const needsApiKey = authType !== AUTH_TYPES.ACCESS_TOKEN;
  const needsAccessToken = [AUTH_TYPES.ACCESS_TOKEN, AUTH_TYPES.API_KEY_ACCESS_TOKEN].includes(authType);
  const needsApiSecret = authType === AUTH_TYPES.API_KEY_SECRET;
  const canSubmit = (!needsApiKey || apiKey.trim()) &&
    (!needsAccessToken || accessToken.trim()) &&
    (!needsApiSecret || apiSecret.trim());
  const hasEnteredCredentials = Boolean(apiKey.trim() || accessToken.trim() || apiSecret.trim());
  const buildCredentialPayload = () => ({
    authType,
    cjAccountEmail: cjAccountEmail.trim(),
    ...(needsApiKey ? { apiKey: apiKey.trim() } : {}),
    ...(needsAccessToken ? { accessToken: accessToken.trim() } : {}),
    ...(needsApiSecret ? { apiSecret: apiSecret.trim() } : {}),
  });
  const authLabel = (type) => ({
    [AUTH_TYPES.API_KEY]: t("h_cj_auth_api_key", "API key (official CJ v2 — recommended)"),
    [AUTH_TYPES.ACCESS_TOKEN]: t("h_cj_auth_access_token", "Access token only"),
    [AUTH_TYPES.API_KEY_ACCESS_TOKEN]: t("h_cj_auth_key_token", "API key + access token"),
    [AUTH_TYPES.API_KEY_SECRET]: t("h_cj_auth_legacy_secret", "Legacy API key + secret"),
  })[type] || type;

  return h(React.Fragment, null,
    h(PageHeader, {
      title: t("h_cj_connect_title", "Connect CJ Dropshipping"),
      description: t("h_cj_connect_desc", "Your key is encrypted on the Elakay server and is never returned to this page."),
    }),
    h(ResourceState, { loading: connectionResource.loading, error: connectionResource.error, empty: false, onRetry: connectionResource.reload, t },
      h(React.Fragment, null,
        connection ? h("div", { className: "cjds-alert" },
          h("div", { className: "cjds-row cjds-between" },
            h("span", null, `${t("h_cj_current_connection", "Current connection")}: ${authLabel(connection.authType)}`),
            h(StatusBadge, { status: connection.status, t })
          )
        ) : null,
        success ? h("div", { className: "cjds-success" }, success) : null,
        error ? h("div", { className: "cjds-alert", role: "alert" }, error) : null,
        h("form", { className: "cjds-form", onSubmit: (event) => {
          event.preventDefault();
          execute(() => api.connectCJ(buildCredentialPayload()), t("h_cj_connected_success", "CJ Dropshipping is connected."));
        } },
          h("div", { className: "cjds-field" },
            h("label", { htmlFor: "cj-account-email" }, t("h_cj_account_email", "CJ account email (optional)")),
            h("input", { id: "cj-account-email", className: "cjds-input", type: "email", autoComplete: "email", value: cjAccountEmail, onChange: (event) => setCjAccountEmail(event.target.value), placeholder: t("h_cj_account_email_placeholder", "email used on CJ Dropshipping") })
          ),
          h("div", { className: "cjds-field" },
            h("label", { htmlFor: "cj-auth-type" }, t("h_cj_authentication_type", "CJ authentication method")),
            h("select", {
              id: "cj-auth-type",
              className: "cjds-input",
              value: authType,
              onChange: (event) => {
                authTypeWasSelected.current = true;
                setAuthType(event.target.value);
                setApiKey(""); setAccessToken(""); setApiSecret("");
              },
            }, ...supportedTypes.map((type) => h("option", { key: type, value: type }, authLabel(type))))
          ),
          needsApiKey ? h("div", { className: "cjds-field" },
            h("label", { htmlFor: "cj-api-key" }, t("h_cj_api_key", "CJ API key")),
            h("input", { id: "cj-api-key", className: "cjds-input", type: "password", autoComplete: "new-password", required: true, value: apiKey, onChange: (event) => setApiKey(event.target.value), placeholder: t("h_cj_api_key_placeholder", "Paste your CJ API key") }),
            authType === AUTH_TYPES.API_KEY ? h("small", { className: "cjds-muted" }, t("h_cj_api_key_help", "CJ v2 exchanges this key for an access token. No API secret is needed.")) : null
          ) : null,
          needsAccessToken ? h("div", { className: "cjds-field" },
            h("label", { htmlFor: "cj-access-token" }, t("h_cj_access_token", "CJ access token")),
            h("input", { id: "cj-access-token", className: "cjds-input", type: "password", autoComplete: "new-password", required: true, value: accessToken, onChange: (event) => setAccessToken(event.target.value), placeholder: t("h_cj_access_token_placeholder", "Paste your CJ-Access-Token") }),
            h("small", { className: "cjds-muted" }, t("h_cj_access_token_help", "The token is sent once to Elakay, encrypted, and never returned to the browser."))
          ) : null,
          needsApiSecret ? h("div", { className: "cjds-field" },
            h("label", { htmlFor: "cj-api-secret" }, t("h_cj_api_secret", "CJ API secret")),
            h("input", { id: "cj-api-secret", className: "cjds-input", type: "password", autoComplete: "new-password", required: true, value: apiSecret, onChange: (event) => setApiSecret(event.target.value) }),
            h("small", { className: "cjds-muted" }, t("h_cj_api_secret_help", "Use this legacy mode only when CJ explicitly provides and requires a secret."))
          ) : null,
          h("div", { className: "cjds-row" },
            h(Button, { type: "submit", disabled: busy || !canSubmit }, busy ? t("h_cj_connecting", "Connecting…") : t("h_cj_connect", "Connect and test")),
            (connection || hasEnteredCredentials) ? h(Button, {
              variant: "secondary",
              disabled: busy || (hasEnteredCredentials ? !canSubmit : !connection),
              onClick: () => execute(
                () => api.testConnection(hasEnteredCredentials ? buildCredentialPayload() : {}),
                t("h_cj_test_success", "The CJ connection is working."),
                { clearCredentials: false, reload: !hasEnteredCredentials }
              ),
            }, hasEnteredCredentials
              ? t("h_cj_test_provided", "Test these credentials")
              : t("h_cj_test_connection", "Test saved connection")) : null,
            connection ? h(Button, { variant: "danger", disabled: busy, onClick: () => { if (window.confirm(t("h_cj_confirm_disconnect", "Disconnect CJ Dropshipping? Imported products will remain in Elakay."))) execute(api.disconnectCJ, t("h_cj_disconnected", "CJ connection disabled.")); } }, t("h_cj_disconnect", "Disconnect")) : null
          )
        )
      )
    ),

    h(PageHeader, {
      title: t("h_cj_payment_mode_title", "Mode de paiement des commandes CJ"),
    }),
    h(ResourceState, { loading: paymentSettingsResource.loading, error: paymentSettingsResource.error, empty: false, onRetry: paymentSettingsResource.reload, t },
      h(React.Fragment, null,
        h("div", { className: "cjds-row cjds-between" },
          h("span", { className: "cjds-badge" },
            `${t("h_cj_payment_mode_active_badge", "Mode actif")}: ${paymentMode === PAYMENT_MODES.AUTOMATIC
              ? t("h_cj_payment_mode_automatic_label", "Automatique")
              : t("h_cj_payment_mode_manual_label", "Manuel")}`)
        ),
        !isCJConnected ? h("div", { className: "cjds-alert", role: "alert" },
          t("h_cj_payment_mode_not_connected", "Votre compte CJ Dropshipping n'est pas connecté. Connectez-le ci-dessus pour pouvoir activer le paiement automatique.")
        ) : null,
        paymentSuccess ? h("div", { className: "cjds-success" }, paymentSuccess) : null,
        paymentError ? h("div", { className: "cjds-alert", role: "alert" }, paymentError) : null,
        h("div", { className: "cjwiz-mode-cards" },
          h("label", { className: `cjwiz-mode-card${paymentMode === PAYMENT_MODES.MANUAL ? " cjwiz-mode-card--active" : ""}` },
            h("input", {
              type: "radio", name: "cj-payment-mode",
              checked: paymentMode === PAYMENT_MODES.MANUAL,
              onChange: () => { setPaymentMode(PAYMENT_MODES.MANUAL); setPaymentModeTouched(true); },
            }),
            h("div", null,
              h("strong", null, t("h_cj_payment_mode_manual_title", "Paiement manuel")),
              h("p", null, t(
                "h_cj_payment_mode_manual_desc",
                "Après la synchronisation d'une commande vers CJ Dropshipping, vous devrez vérifier et payer personnellement la commande depuis votre compte CJ. Aucun paiement automatique ne sera tenté."
              ))
            )
          ),
          h("label", {
            className: `cjwiz-mode-card${paymentMode === PAYMENT_MODES.AUTOMATIC ? " cjwiz-mode-card--active" : ""}`,
            style: !isCJConnected ? { opacity: 0.55, cursor: "not-allowed" } : undefined,
          },
            h("input", {
              type: "radio", name: "cj-payment-mode",
              disabled: !isCJConnected,
              checked: paymentMode === PAYMENT_MODES.AUTOMATIC,
              onChange: () => { setPaymentMode(PAYMENT_MODES.AUTOMATIC); setPaymentModeTouched(true); },
            }),
            h("div", null,
              h("strong", null, t("h_cj_payment_mode_automatic_title", "Paiement automatique")),
              h("p", null, t(
                "h_cj_payment_mode_automatic_desc",
                "Après le paiement du client, Elakay tentera de traiter automatiquement la commande avec votre compte CJ. Si le paiement automatique échoue ou si votre solde CJ est insuffisant, la commande passera en attente de paiement manuel."
              ))
            )
          )
        ),
        h("div", { className: "cjds-row" },
          h(Button, { disabled: paymentBusy || (!paymentModeTouched), onClick: savePaymentMode },
            paymentBusy ? t("h_cj_payment_mode_saving", "Enregistrement…") : t("h_cj_payment_mode_save", "Enregistrer"))
        ),
        h("div", { className: "cjds-field", style: { marginTop: 16 } },
          h("label", null, t("h_cj_payment_wallet_title", "Solde CJ Wallet")),
          wallet?.supported
            ? h("p", null, `${wallet.lastKnownBalance ?? "?"} ${wallet.currency || ""} (${t("h_cj_payment_wallet_checked_on", "vérifié le")} ${wallet.lastCheckedAt ? new Date(wallet.lastCheckedAt).toLocaleString() : "?"})`)
            : h("p", { className: "cjds-muted" }, t("h_cj_payment_wallet_unknown", "Solde à vérifier sur CJ Dropshipping")),
          walletError ? h("div", { className: "cjds-alert", role: "alert" }, walletError) : null,
          h(Button, { variant: "secondary", disabled: walletChecking || !isCJConnected, onClick: checkWalletBalance },
            walletChecking ? t("h_cj_payment_wallet_checking", "Vérification…") : t("h_cj_payment_wallet_check", "Vérifier le solde"))
        ),
        h("small", { className: "cjds-muted", style: { display: "block", marginTop: 14 } },
          t("h_cj_payment_mode_seller_responsibility", "Vous restez responsable du financement de vos commandes CJ Dropshipping (CJ Wallet, carte, ou tout autre moyen disponible dans votre compte CJ).")),
        h("small", { className: "cjds-muted", style: { display: "block", marginTop: 4 } },
          t("h_cj_payment_mode_no_guarantee", "Elakay ne garantit pas la disponibilité de votre solde CJ Wallet."))
      )
    )
  );
};

module.exports = { DropshippingCJConnect };
